/**
 * 画像バリデーションサービス
 * グレースケール画像のバリデーションを行う
 */

export interface ImageValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    width?: number;
    height?: number;
}

/**
 * 画像バリデーションサービスクラス
 */
export class ImageValidationService {
    // 期待される画像サイズ（mm）
    private static readonly EXPECTED_WIDTH_MM = 20.1;
    private static readonly EXPECTED_HEIGHT_MM = 40.6;
    private static readonly MM_TO_INCH = 25.4;

    // 推奨DPI
    private static readonly RECOMMENDED_DPI = 500;

    // サイズの許容誤差（mm単位での誤差）
    private static readonly SIZE_TOLERANCE_MM = 0.1; // 0.1mm

    /**
     * ファイルがPNG形式かどうかをチェック
     */
    private static isPngFile(file: File): boolean {
        return file.type === 'image/png';
    }

    /**
     * 画像がグレースケールかどうかをチェック
     * @param imageData ImageDataオブジェクト
     * @returns グレースケールの場合true
     */
    private static isGrayscale(imageData: ImageData): boolean {
        const data = imageData.data;
        const threshold = 5; // RGB値の許容誤差

        // サンプリング：全ピクセルをチェックすると重いので、一定間隔でサンプリング
        const sampleInterval = 10;

        for (let i = 0; i < data.length; i += 4 * sampleInterval) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            // RGB値がほぼ等しくない場合はカラー画像
            if (Math.abs(r - g) > threshold || Math.abs(g - b) > threshold || Math.abs(r - b) > threshold) {
                return false;
            }
        }

        return true;
    }

    /**
     * ピクセルサイズからmm単位のサイズを計算（指定されたDPIで）
     */
    private static pxToMm(px: number, dpi: number): number {
        return (px * this.MM_TO_INCH) / dpi;
    }

    /**
     * 特定のDPI値で画像のサイズが期待値と一致するかチェック
     * @param width 画像の幅（ピクセル）
     * @param height 画像の高さ（ピクセル）
     * @param dpi チェックするDPI値
     * @returns チェック結果
     */
    private static checkSizeWithDpi(width: number, height: number, dpi: number): {
        isValid: boolean;
        detectedDpi: number;
        calculatedWidthMm: number;
        calculatedHeightMm: number;
        isRecommendedDpi: boolean;
        isFromMetadata: boolean;
    } {
        const calculatedWidthMm = this.pxToMm(width, dpi);
        const calculatedHeightMm = this.pxToMm(height, dpi);

        const widthDiff = Math.abs(calculatedWidthMm - this.EXPECTED_WIDTH_MM);
        const heightDiff = Math.abs(calculatedHeightMm - this.EXPECTED_HEIGHT_MM);

        return {
            isValid: widthDiff <= this.SIZE_TOLERANCE_MM && heightDiff <= this.SIZE_TOLERANCE_MM,
            detectedDpi: dpi,
            calculatedWidthMm,
            calculatedHeightMm,
            isRecommendedDpi: dpi === this.RECOMMENDED_DPI,
            isFromMetadata: true
        };
    }

    /**
     * 画像ファイルをバリデート
     * @param file アップロードされた画像ファイル
     * @returns バリデーション結果
     */
    static async validateImageFile(file: File): Promise<ImageValidationResult> {
        const errors: string[] = [];
        const warnings: string[] = [];

        // 1. ファイル形式チェック
        if (!this.isPngFile(file)) {
            errors.push('PNG形式の画像ファイルを選択してください');
            return { isValid: false, errors, warnings };
        }

        // 2. メタデータからDPI値を取得
        const metadataDpi = await this.extractDpiFromFile(file);

        // 3. 画像を読み込んでサイズとグレースケールをチェック
        try {
            const imageData = await this.loadImageData(file);
            const { width, height, data } = imageData;

            // DPI値の決定（メタデータがない場合は推奨DPIを使用）
            const dpi = metadataDpi !== null ? metadataDpi : this.RECOMMENDED_DPI;

            // メタデータにDPI情報がない場合は警告
            if (metadataDpi === null) {
                warnings.push(
                    `画像にDPI情報が含まれていません。500dpiとして処理されます。`
                );
            }

            // サイズチェック
            const sizeCheck = this.checkSizeWithDpi(width, height, dpi);

            if (!sizeCheck.isValid) {
                errors.push(
                    `画像サイズが正しくありません。\n` +
                    `正しいサイズ: ${this.EXPECTED_WIDTH_MM}×${this.EXPECTED_HEIGHT_MM}mm\n` +
                    `実際のサイズ: ${sizeCheck.calculatedWidthMm.toFixed(1)}×${sizeCheck.calculatedHeightMm.toFixed(1)}mm\n`
                );
            } else {
                // メタデータにDPI情報があり、推奨DPIでない場合はエラー
                if (metadataDpi !== null && !sizeCheck.isRecommendedDpi) {
                    errors.push(
                        `DPIが500dpiではありません。（${dpi}dpi）\n` +
                        `500dpiの画像を使用してください。`
                    );
                }
            }

            // グレースケールチェック
            if (!this.isGrayscale(data)) {
                errors.push('グレースケール画像を選択してください。カラー画像は使用できません');
            }

            return {
                isValid: errors.length === 0,
                errors,
                warnings,
                width,
                height
            };
        } catch (error) {
            errors.push(`画像の読み込みに失敗しました: ${error instanceof Error ? error.message : String(error)}`);
            return { isValid: false, errors, warnings };
        }
    }

    /**
     * PNGファイルからpHYsチャンクを読み取ってDPI値を取得
     * @param arrayBuffer PNGファイルのArrayBuffer
     * @returns DPI値（見つからない場合はnull）
     */
    private static extractDpiFromPng(arrayBuffer: ArrayBuffer): number | null {
        const view = new DataView(arrayBuffer);

        // PNGシグネチャの確認（8バイト）
        const pngSignature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
        for (let i = 0; i < 8; i++) {
            if (view.getUint8(i) !== pngSignature[i]) {
                return null; // 有効なPNGファイルではない
            }
        }

        let offset = 8; // シグネチャの後から開始

        // チャンクを順に読み取る
        while (offset < view.byteLength) {
            // チャンク長を読み取る（4バイト、ビッグエンディアン）
            const length = view.getUint32(offset);
            offset += 4;

            // チャンクタイプを読み取る（4バイト）
            const type = String.fromCharCode(
                view.getUint8(offset),
                view.getUint8(offset + 1),
                view.getUint8(offset + 2),
                view.getUint8(offset + 3)
            );
            offset += 4;

            // pHYsチャンクを見つけた場合
            if (type === 'pHYs') {
                // pHYsチャンクのデータ構造:
                // 4バイト: X軸のピクセル/ユニット
                // 4バイト: Y軸のピクセル/ユニット
                // 1バイト: ユニット指定子（0=不明、1=メートル）

                const pixelsPerUnitX = view.getUint32(offset);
                const pixelsPerUnitY = view.getUint32(offset + 4);
                const unit = view.getUint8(offset + 8);

                // ユニットがメートルの場合、DPIに変換
                if (unit === 1) {
                    // ピクセル/メートル → DPI (1インチ = 0.0254メートル)
                    const dpiX = Math.round(pixelsPerUnitX * 0.0254);
                    const dpiY = Math.round(pixelsPerUnitY * 0.0254);

                    // X軸とY軸のDPIが一致する場合のみ返す
                    if (dpiX === dpiY) {
                        return dpiX;
                    }
                }

                return null; // ユニットが不明、またはX/Y軸のDPIが不一致
            }

            // 次のチャンクへ移動（データ長 + 4バイトのCRC）
            offset += length + 4;

            // IENDチャンクに到達したら終了
            if (type === 'IEND') {
                break;
            }
        }

        return null; // pHYsチャンクが見つからなかった
    }

    /**
     * ファイルからDPI値を取得（メタデータから）
     * @param file PNGファイル
     * @returns DPI値（見つからない場合はnull）
     */
    private static async extractDpiFromFile(file: File): Promise<number | null> {
        return new Promise((resolve) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const arrayBuffer = e.target?.result as ArrayBuffer;
                const dpi = this.extractDpiFromPng(arrayBuffer);
                resolve(dpi);
            };

            reader.onerror = () => {
                resolve(null);
            };

            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * 画像ファイルを読み込んでImageDataを取得
     */
    private static loadImageData(file: File): Promise<{ width: number; height: number; data: ImageData }> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const img = new Image();

                img.onload = () => {
                    try {
                        // Canvas上に画像を描画してImageDataを取得
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;

                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            reject(new Error('Canvas 2Dコンテキストの取得に失敗しました'));
                            return;
                        }

                        ctx.drawImage(img, 0, 0);
                        const imageData = ctx.getImageData(0, 0, img.width, img.height);

                        resolve({
                            width: img.width,
                            height: img.height,
                            data: imageData
                        });
                    } catch (error) {
                        reject(error);
                    }
                };

                img.onerror = () => {
                    reject(new Error('画像の読み込みに失敗しました'));
                };

                img.src = e.target?.result as string;
            };

            reader.onerror = () => {
                reject(new Error('ファイルの読み込みに失敗しました'));
            };

            reader.readAsDataURL(file);
        });
    }

    /**
     * 画像ファイルをbase64エンコードされたデータURLに変換
     */
    static async fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                resolve(e.target?.result as string);
            };

            reader.onerror = () => {
                reject(new Error('ファイルの読み込みに失敗しました'));
            };

            reader.readAsDataURL(file);
        });
    }
}
